import { useState } from 'react';
import { Equalizer } from '@eqi-investimentos/icons/web';
import { Box, Input, Flex, Text } from '@eqi-investimentos/react';
import { useT } from '@/libs/i18n/utils/useT';
import { useGetAssets } from '@/modules/Asset/http/client/useGetAssets';
import { useInvest } from '@/contexts/InvestContext';
import { useDebounce } from '@/hooks/useDebounce';
import { Icon } from '@/components/elements/Icon';
import { Thumb } from '@/components/elements/Thumb';
import { IfRender, MapRender } from '@/utils/jsx';
import { SelectAssetSectionSkeleton } from './Skeleton';

export const SelectAssetSection = () => {
  const t = useT();
  const { selectAsset } = useInvest();

  const [searchParam, setSearchParam] = useState('');

  const debouncedSearchParam = useDebounce(searchParam);

  const { data: assets = [], status: assetsStatus } = useGetAssets({
    search: debouncedSearchParam,
    page: 1,
    limit: 15,
  });

  const [assetToSearch, setAssetToSearch] = useState('');

  const loadingAssets = assetsStatus === 'pending';

  return (
    <Box w="full">
      <Flex alignItems="center" w="full">
        <Input
          placeholder={t('screens.invest.selectAsset.searchPlaceholder')}
          type="text"
          value={assetToSearch}
          onChange={(e) => {
            setAssetToSearch(e.target.value);
            setSearchParam(() => e.target.value);
          }}
          rightIcon={
            <Icon
              iconName={assetToSearch.length === 0 ? 'Search' : 'Trash'}
              width={24}
              height={24}
              color="content.brand"
              onClick={() => {
                setAssetToSearch('');
                setSearchParam(() => '');
              }}
            />
          }
        />
      </Flex>
      <IfRender condition={loadingAssets}>
        <SelectAssetSectionSkeleton />
      </IfRender>
      <IfRender condition={!loadingAssets}>
        <Box py="xl">
          <IfRender condition={assets.length === 0}>
            <Flex
              alignItems="center"
              justifyContent="space-be"
              gap="5"
              w="22.375rem"
              h="5.625rem"
              px="4"
              py="3"
              bgColor="content.alwaysLight"
              borderRadius="lg"
            >
              <Equalizer width={7} height={7} fill="content.primary" />
              <Text fontWeight="bold" lineHeight="4" color="content.secondary">
                {t('screens.invest.selectAsset.noAssets')}
              </Text>
            </Flex>
          </IfRender>
          <IfRender condition={assets.length > 0}>
            <Flex flexDir="column" gap="xs" py="xl">
              <MapRender
                items={assets}
                render={(asset) => (
                  <Flex
                    flexDir="row"
                    w="full"
                    key={asset.id}
                    justify="space-between"
                    alignItems="center"
                    py="s"
                    px="xs"
                    onClick={() => selectAsset({ asset })}
                    cursor="pointer"
                    _hover={{
                      background: 'background.brandLight',
                      borderRadius: 'xs',
                    }}
                  >
                    <Flex gap="xs" flexDirection="row" w="full">
                      <Thumb
                        src={asset.thumb || ''}
                        width={32}
                        height={32}
                        fit="cover"
                        alt={asset.title}
                      />
                      <Text size="l" color="content.primary">
                        {asset.title}
                      </Text>
                    </Flex>
                    <Icon
                      iconName="ChevronRight"
                      w={24}
                      h={24}
                      color="content.brand"
                    />
                  </Flex>
                )}
              />
            </Flex>
          </IfRender>
        </Box>
      </IfRender>
    </Box>
  );
};
